import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  AfterViewInit,
  OnDestroy,
  ElementRef,
} from "@angular/core";
import { Table, TableModule } from "primeng/table";
import {
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  Subject,
  switchMap,
} from "rxjs";
import buildQuery from "odata-query";
import { FormGroup, FormsModule } from "@angular/forms";
import { CommonModule, DatePipe } from "@angular/common";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Address } from "../../../core/models/shared/address";
import { Currency } from "../../../core/models/shared/currencyList";
import { ReleasedProduct } from "../../../core/models/shared/releaseProduct";
import { ShippingMethod } from "../../../core/models/shared/shippingMethod";
import { GetAllVendorsDto } from "../../../core/models/vendor/getAllVendors";
import { AddNewClientService } from "../../../core/services/add-new-client.service";
import { LoaderService } from "../../../core/services/loader.service";
import { OrderLineItemDetailService } from "../../../core/services/order-line-item-detail.service";
import { SamplepoListingService } from "../../../core/services/samplepo-listing.service";
import { JniToastrService } from "../../../core/services/jni-toastr.service";
import { AccordionModule } from "primeng/accordion";
import { HidedynamicsdatePipe } from "../../../shared/pipes/hidedate/hidedynamicsdate.pipe";
import { CheckboxModule } from "primeng/checkbox";
import { AddressAssignmentComponent } from "../address-assignment/address-assignment.component";
import { SelectChangeEvent, SelectModule } from "primeng/select";
import { Popover, PopoverModule } from "primeng/popover";
import { ExchangeRatesDto } from "../../../core/models/shared/exchangeRateDto";
import { DatePicker, DatePickerModule } from "primeng/datepicker";
import { PurchaseOrderLine } from "../../../core/models/samplePO/details/purchaseOrderLine";
import { PurchaseOrderHeader } from "../../../core/models/samplePO/details/purchaseOrderHeader";
import { PanelModule } from "primeng/panel";
import { ButtonModule } from "primeng/button";
import { CardModule } from "primeng/card";
import { InputTextModule } from "primeng/inputtext";
import { TooltipModule } from "primeng/tooltip";

@Component({
  selector: "jni-inventory-purchase-line-services",
  templateUrl: "./inventory-purchase-line-services.component.html",
  styleUrls: ["./inventory-purchase-line-services.component.scss"],
  imports: [
    CommonModule,
    AccordionModule,
    TableModule,
    FormsModule,
    HidedynamicsdatePipe,
    CheckboxModule,
    AddressAssignmentComponent,
    SelectModule,
    PopoverModule,
    DatePickerModule,
    PanelModule,
    ButtonModule,
    CardModule,
    InputTextModule,
    TooltipModule,
  ],
})
@UntilDestroy()
export class InventoryPurchaseLineServicesComponent
  implements OnInit, OnChanges, AfterViewInit, OnDestroy
{
  filteredServiceLines: PurchaseOrderLine[] = [];
  @Input() shippingProductsList: ShippingMethod[] = [];
  @Input() customerId = "";
  @Input() serviceLines: PurchaseOrderLine[] = [];
  @Input() selectedLineGroup = "";
  @Input() currencyConversion!: ExchangeRatesDto;
  @Input() purchaseOrderHeader: PurchaseOrderHeader = {} as PurchaseOrderHeader;
  @Input() purchaseOrderHeaderAddress: Address = {} as Address;
  @Input() site = "";
  @Input() warehouse = "";
  @Input() purchaseOrderNumber = "";
  @Input() parent!: FormGroup;
  @Output() rowSaveEvent = new EventEmitter<PurchaseOrderLine>();
  @Output() copyLinesEvent = new EventEmitter<PurchaseOrderLine[]>();
  @Output() deleteLinesEvent = new EventEmitter<PurchaseOrderLine[]>();
  @Output() addNewServiceLine = new EventEmitter();
  @Output() setServiceItemLine = new EventEmitter<PurchaseOrderLine>();
  @Output() saveLines = new EventEmitter<void>();

  toolTipOptions = {
    autoHide: false,
    escape: false,
    tooltipEvent: "hover" as const,
    tooltipPosition: "top" as const,
    tooltipStyleClass: "tooltipwidth",
  };

  selectedServiceLines: PurchaseOrderLine[] = [];
  items: ReleasedProduct[] = [];
  unit = ["ea", "cs"];
  shipDateCurrentdate = new Date();
  descriptionCharacterCond = false;
  currencyList: Currency[] = [];
  vendorAccountNumber = "";
  vendorList: GetAllVendorsDto[] = [];

  netTotalPrice = "";
  netTotalPriceConverted = "";
  isoCurrencyCode = "";
  netReteAfterConversion = 0;
  netConvertToUsd = "";
  displayAddressAssignment = false;
  currDateTime = "";
  nextDateTime = "";
  public searchVendor$ = new Subject<string>();
  @ViewChild("dt") table!: Table;
  lastKeyWasArrow = false;

  ngOnInit(): void {
    this.currDateTime = this.formatDate(new Date());
    this.nextDateTime = this.formatDate(
      new Date(new Date().setDate(new Date().getDate() + 1))
    );
    this.getAllReleaseProduct();
    this.getCurrencyListData();
    this.searchVendorName();
  }

  constructor(
    private _samplepoListingService: SamplepoListingService,
    private _addNewClientService: AddNewClientService,
    private _loaderService: LoaderService,
    private _toastr: JniToastrService,
    private _orderLineItemDetailService: OrderLineItemDetailService,
    public datepipe: DatePipe,
    private _elementRef: ElementRef<HTMLElement>
  ) {}

  ngAfterViewInit(): void {
    // p-select v19 calls event.stopPropagation() on every Tab keydown, which
    // blocks pEditableColumn's built-in Tab-to-next-cell navigation. We
    // intercept Tab in the CAPTURE phase (before p-select's handler runs) and
    // manually trigger the click on the next .p-editable-column td, which
    // opens it for editing and auto-focuses its first input/textarea.
    this._elementRef.nativeElement.addEventListener(
      "keydown",
      this._onEditableCellTabCapture,
      true
    );
  }

  ngOnDestroy(): void {
    this._elementRef.nativeElement.removeEventListener(
      "keydown",
      this._onEditableCellTabCapture,
      true
    );
  }

  private _onEditableCellTabCapture = (event: KeyboardEvent): void => {
    if (event.key !== "Tab") return;
    const target = event.target as HTMLElement | null;
    // Only act when Tab happens inside a cell that is currently being edited
    const currentTd = target?.closest(
      "td.p-editable-column.p-cell-editing"
    ) as HTMLElement | null;
    if (!currentTd) return;

    const direction = event.shiftKey
      ? "previousElementSibling"
      : "nextElementSibling";
    let nextTd = currentTd[direction] as HTMLElement | null;
    while (nextTd && !nextTd.classList.contains("p-editable-column")) {
      nextTd = nextTd[direction] as HTMLElement | null;
    }
    if (!nextTd) return;

    event.preventDefault();
    event.stopPropagation();
    // Click the target cell to trigger pEditableColumn's onClick -> openCell(),
    // which opens the edit template and auto-focuses input/textarea/select.
    nextTd.click();
  };

  ngOnChanges(changes: SimpleChanges) {
    this.lineGroupChanges(changes);
    this.serviceLinesChanges(changes);
  }

  lineGroupChanges(changes: SimpleChanges) {
    if (changes["selectedLineGroup"]) {
      this.filterServiceLines();
    }
  }

  serviceLinesChanges(changes: SimpleChanges) {
    if (changes["serviceLines"]) {
      this.filterServiceLines();
    }
  }

  filterServiceLines() {
    this.selectedServiceLines = [];
    this.filteredServiceLines = this.serviceLines
      .filter(x => x.lineGroupD365 === this.selectedLineGroup)
      .map(x => ({
        ...x,
        requestedShippingDateD365: this.toDate(x.requestedShippingDateD365),
        requestedDeliveryDateD365: this.toDate(x.requestedDeliveryDateD365),
      }));
  }

  toDate(val: string | Date | null): string | Date | null {
    if (!val) return null;
    if (val instanceof Date) return isNaN(val.getTime()) ? null : val;
    if (typeof val === "string") {
      // MM/dd/yyyy support
      const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(val.trim());
      if (m) return new Date(+m[3], +m[1] - 1, +m[2]);
      // ISO (yyyy-mm-dd) fallback
      const d = new Date(val);
      const t = isNaN(d.getTime()) ? null : d;
      return this.datepipe.transform(t, "MM/dd/yyyy");
    }
    return null;
  }
  addNewService() {
    if (!this.selectedLineGroup) {
      this._toastr.error("Error", "Select a line group first");
      return;
    }

    this.addNewServiceLine.emit();

    // this.cancelAllEditRow();
    // const object = {
    //   id: 0,
    //   lineRecIdD365: null,
    //   siteD365: this.site,
    //   warehouseD365: this.warehouse,
    //   jobNameD365: this.parent.get('jobName')?.value,
    //   wMSLocationD365: this.parent.get('location')?.value,
    //   portalKeyD365: uuidv4(),
    //   parentD365: false,
    //   lineGroupD365: this.selectedLineGroup,
    //   purchaseOrderNumberD365: this.purchaseOrderNumber,
    //   purchaseUnitSymbolD365: 'ea'
    // } as PurchaseOrderLine;
    // this.filteredServiceLines.push(object);
    // this.serviceLines.push(object);
    // this.table.initRowEdit(this.table.value[this.table.value?.length - 1]);
  }

  onCopyLines() {
    const selectedPoLines = [...this.selectedServiceLines];
    const portalOnlyLines = this.selectedServiceLines.filter(x => !x.id);
    selectedPoLines.push(...portalOnlyLines);
    if (selectedPoLines.length <= 0) {
      this._toastr.error("Error", "No lines selected");
      return;
    }
    this.selectedServiceLines = [];
    this.copyLinesEvent.emit(selectedPoLines);
  }

  onDeleteLines() {
    const selectedPoLines = [...this.selectedServiceLines];
    const selectedLinesIdNotInPortal = selectedPoLines
      .filter(x => !x.id && x.portalKeyD365)
      .map(x => x.portalKeyD365);

    this.filteredServiceLines = this.filteredServiceLines.filter(
      x => !selectedLinesIdNotInPortal.includes(x.portalKeyD365)
    );

    this.selectedServiceLines = [];
    this.deleteLinesEvent.emit(selectedPoLines);
  }

  clearShippingMethod(line: PurchaseOrderLine) {
    line.freightAccountNumD365 = "";
  }

  onShippingMethodSelect(
    shippingMethodCode: SelectChangeEvent,
    line: PurchaseOrderLine
  ) {
    const index = this.shippingProductsList.findIndex(
      x => x.modeCodeD365 == shippingMethodCode.value
    );

    if (index > -1) {
      const selectedShippingMethod = this.shippingProductsList[index];
      if (
        selectedShippingMethod.useFreightAccountD365?.toLowerCase() == "yes"
      ) {
        line.freightAccountNumD365 =
          selectedShippingMethod.jniFreightAcctNumD365;
      } else {
        this._orderLineItemDetailService
          .getClientFreightAccountNumber(this.customerId)
          .subscribe({
            next: res => {
              if (res.data) {
                line.freightAccountNumD365 = res.data;
              } else {
                line.freightAccountNumD365 = "";
              }
            },
            error: () => {
              line.freightAccountNumD365 = "";
            },
          });
      }
    }
  }

  addressAssignment() {
    this.displayAddressAssignment = true;
  }

  onRowEditSave(purchaseOrderLine: PurchaseOrderLine) {
    // Create a copy for emitting to avoid mutating the original Date objects
    // The parent expects string dates in MM/dd/yyyy format
    const lineToEmit = {
      ...purchaseOrderLine,
      requestedShippingDateD365: this.datepipe.transform(
        purchaseOrderLine.requestedShippingDateD365,
        "MM/dd/yyyy"
      ),
      requestedDeliveryDateD365: this.datepipe.transform(
        purchaseOrderLine.requestedDeliveryDateD365,
        "MM/dd/yyyy"
      ),
    };
    this.rowSaveEvent.emit(lineToEmit as PurchaseOrderLine);
  }

  cancelAllEditRow() {
    this.table.value.forEach(x => {
      this.table.cancelRowEdit(x);
    });
  }

  getAllReleaseProduct() {
    this._samplepoListingService
      .getAllCategory()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          this.items = res.data?.filter(
            x => x.productTypeD365 == "Service" && x.poStopped === "No"
          );
        },
      });
  }

  itemSelected(
    itemNumber: SelectChangeEvent,
    purchaseOrderLine: PurchaseOrderLine
  ) {
    purchaseOrderLine.productName =
      this.items.find(x => x.itemNumberD365 == itemNumber.value)
        ?.productSearchNameD365 ?? "";
  }

  getCurrencyListData() {
    this._addNewClientService
      .getCurrencyList()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          this.currencyList = res.data;
        },
      });
  }

  shipDateChange(event: Date, line: PurchaseOrderLine) {
    const shipDate = new Date(event);
    const formattedShipDate =
      this.datepipe.transform(shipDate, "MM/dd/yyyy") ?? "";
    if (
      line.requestedDeliveryDateD365 &&
      shipDate > new Date(line.requestedDeliveryDateD365)
    ) {
      this._toastr.error(
        "Error",
        "Ship date cannot be later than in-hands date"
      );
      return;
    }
    line.requestedShippingDateD365 = formattedShipDate;
  }

  handleKeyDown(
    event: Event,
    dp: DatePicker,
    line: PurchaseOrderLine,
    field: string
  ): void {
    // Space opens overlay
    if (
      event instanceof InputEvent &&
      event.inputType === "insertText" &&
      event.data === " "
    ) {
      event.preventDefault();
      dp.overlayVisible = true;
      return;
    }

    const el = event.target as HTMLInputElement | null;
    if (!el) return;

    // Handle clearing when input is empty (backspace/delete)
    if (el.value === "" || el.value.trim() === "") {
      this.clearDateField(line, field);
      dp.clear();
      return;
    }

    // keep only digits and '/', normalize '-'
    const raw = (el.value || "")
      .trim()
      .replace(/-/g, "/") // normalize dashes to slashes
      .replace(/[^0-9/]/g, ""); // keep only digits and /

    // rebuild from digits, auto insert '/'
    const digits = raw.replace(/\//g, "").slice(0, 8); // MMddyyyy only
    let formatted = digits;
    if (digits.length >= 3)
      formatted = `${digits.slice(0, 2)}/${digits.slice(2)}`;
    if (digits.length >= 5)
      formatted = `${formatted.slice(0, 5)}/${digits.slice(4)}`;
    el.value = formatted;

    // Clear the date if all digits are removed
    if (digits.length === 0) {
      this.clearDateField(line, field);
      return;
    }

    // only validate when full yyyy is present (8 digits)
    if (digits.length !== 8) return;

    const mm = +digits.slice(0, 2); // month
    const dd = +digits.slice(2, 4); // day
    const yyyy = +digits.slice(4, 8); // year

    const d = new Date(yyyy, mm - 1, dd);
    const isRealDate =
      d.getFullYear() === yyyy && d.getMonth() + 1 === mm && d.getDate() === dd;
    if (!isRealDate) {
      this._toastr.error("Error", "Invalid date value. Expected MM/dd/yyyy");
      return;
    }

    // helpers
    const toYmd = (x: Date) =>
      new Date(x.getFullYear(), x.getMonth(), x.getDate());
    const asDateOrNull = (val: unknown): Date | null => {
      if (val == null) return null;
      if (val instanceof Date) return isNaN(val.getTime()) ? null : toYmd(val);
      if (typeof val === "string") {
        const t = val.trim();
        if (!t || t.toLowerCase() === "null") return null;
        // try MM/dd/yyyy format first
        const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(t);
        if (m) return new Date(+m[3], +m[1] - 1, +m[2]);
        const p = new Date(t);
        return isNaN(p.getTime()) ? null : toYmd(p);
      }
      return null;
    };

    // existing other dates (null-safe)
    const shipExisting = asDateOrNull(line.requestedShippingDateD365);
    const delvExisting = asDateOrNull(line.requestedDeliveryDateD365);

    // figure out comparisons using the new value for the edited field
    const ship =
      field === "requestedShippingDateD365" ? toYmd(d) : shipExisting;
    const delv =
      field === "requestedDeliveryDateD365" ? toYmd(d) : delvExisting;

    // cross-field rules (only when the "other" side exists)
    if (field === "requestedShippingDateD365" && delv && toYmd(d) > delv) {
      this._toastr.error(
        "Error",
        "Ship date cannot be later than in-hands date"
      );
      return;
    }
    if (field === "requestedDeliveryDateD365" && ship && toYmd(d) < ship) {
      this._toastr.error(
        "Error",
        "In-hands date cannot be earlier than Ship date"
      );
      return;
    }

    // min date check for ship date (only if valid min provided)
    if (field === "requestedShippingDateD365") {
      const min = asDateOrNull(this.shipDateCurrentdate);
      if (min && toYmd(d) < min) {
        this._toastr.error(
          "Error",
          "Ship date cannot be earlier than the minimum allowed date"
        );
        return;
      }
    }

    // update UI + model
    dp.updateModel?.(d);
    if (field === "requestedShippingDateD365") {
      line.requestedShippingDateD365 = d;
    } else {
      line.requestedDeliveryDateD365 = d;
    }
  }

  onDateModelChange(
    value: Date | null | undefined,
    line: PurchaseOrderLine,
    field: string
  ): void {
    // Only update if the value is a valid Date - let onSelect/onClear handle specific cases
    // Ignore undefined values that occur during datepicker initialization
    if (value === undefined) {
      return;
    }
    if (value === null) {
      this.clearDateField(line, field);
    } else if (value instanceof Date && !isNaN(value.getTime())) {
      // Valid date - update the field
      if (field === "requestedShippingDateD365") {
        line.requestedShippingDateD365 = value;
      } else if (field === "requestedDeliveryDateD365") {
        line.requestedDeliveryDateD365 = value;
      }
    }
  }

  clearDateField(line: PurchaseOrderLine, field: string): void {
    if (field === "requestedShippingDateD365") {
      line.requestedShippingDateD365 = null as unknown as Date;
    } else if (field === "requestedDeliveryDateD365") {
      line.requestedDeliveryDateD365 = null as unknown as Date;
    }
  }

  refocusById(id: string) {
    setTimeout(
      () => (document.getElementById(id) as HTMLInputElement | null)?.focus(),
      0
    );
  }

  changeFormat(date: Date | string | null): Date | string | null {
    if (!date || typeof date === "object") return date;
    const d = new Date(date);
    if (isNaN(d.getTime())) return "";
    return d ?? "";
  }

  deliveryDateChange(event: Date, line: PurchaseOrderLine) {
    const inHandDate = new Date(event);
    const formattedInHandDate =
      this.datepipe.transform(inHandDate, "MM/dd/yyyy") ?? "";
    if (
      line.requestedShippingDateD365 &&
      inHandDate < new Date(line.requestedShippingDateD365)
    ) {
      this._toastr.error(
        "Error",
        "In-hands date cannot be earlier than Ship date"
      );
      return;
    } else {
      line.requestedDeliveryDateD365 = formattedInHandDate;
    }
  }

  descriptionCharacterCount(obj: string) {
    if (obj && obj?.length > 45) {
      this.descriptionCharacterCond = true;
    }
  }

  patchValueInTotalNet(
    net: string,
    convertedNet: string,
    lineQuantity: string,
    lineObject: PurchaseOrderLine
  ) {
    const quantity = lineQuantity;
    if (typeof quantity == "number" || quantity) {
      const netprice = parseFloat(net) * parseInt(quantity);
      this.netTotalPrice = netprice.toString();
      lineObject.lineAmountD365 = parseFloat(this.netTotalPrice);
      const netPriceCon =
        parseFloat(convertedNet == "" ? "0" : convertedNet) *
        parseInt(quantity);
      this.netTotalPriceConverted = netPriceCon.toString();
      lineObject.netConversionTotal = parseFloat(
        this.netTotalPriceConverted ?? "0"
      );

      if (
        this.netTotalPriceConverted &&
        this.netTotalPriceConverted != "0" &&
        lineObject?.currencyCodeD365?.toLowerCase() != "usd"
      ) {
        lineObject.amountInTransactionCurrency = parseFloat(
          this.netTotalPriceConverted ?? "0"
        );
      } else {
        lineObject.amountInTransactionCurrency = lineObject.lineAmountD365;
      }
    }
  }

  calculateQuantity(event: number, purchaseOrderLine: PurchaseOrderLine) {
    //const quantity = event;
    let quantity = event.toString().replace(/[^-0-9]/g, "");
    quantity = quantity.substring(0, 5);
    if (quantity) {
      purchaseOrderLine.orderedPurchaseQuantityD365 = parseInt(quantity);
      const netPrice = purchaseOrderLine.purchasePriceD365;
      if (netPrice) {
        //netPrice = netPrice.replace(/[^a-zA-Z0-9.]/g, '');
        const convertedNetPrice = purchaseOrderLine.netConversion;
        this.patchValueInTotalNet(
          netPrice.toString(),
          convertedNetPrice?.toString() ?? "",
          purchaseOrderLine.orderedPurchaseQuantityD365.toString(),
          purchaseOrderLine
        );
      }
    } else {
      purchaseOrderLine.orderedPurchaseQuantityD365 = parseInt(quantity);
    }

    this.onNetConversion(
      purchaseOrderLine.purchasePriceD365 ?? 0,
      purchaseOrderLine
    );
  }

  onNetConversion(event: number, lineObject: PurchaseOrderLine) {
    let netRate = event.toString().replace(/[^0-9.]/g, "");
    const regExp = /^(\d+(\.\d*)?)$/;
    const revenue = regExp.test(netRate) ? "valid" : "invalid";
    if (lineObject.currencyCodeD365?.toLowerCase() !== "usd") {
      this.onCurrencyConversion(lineObject);
    }
    if (revenue === "valid") {
      netRate = netRate.substring(0, 9);
      lineObject.purchasePriceD365 = parseFloat(netRate);
      if (netRate) {
        if (
          this.currencyConversion &&
          lineObject?.currencyCodeD365?.toLowerCase() != "usd"
        ) {
          if (this.currencyConversion.Rate != 0) {
            this.netReteAfterConversion =
              parseFloat(netRate) * this.currencyConversion?.Rate;
            if (this.netReteAfterConversion.toString() != "") {
              this.netConvertToUsd = this.netReteAfterConversion.toString();
              lineObject.netConversion = parseFloat(
                this.netConvertToUsd ?? "0"
              );

              if (
                this.netConvertToUsd &&
                lineObject?.currencyCodeD365?.toLowerCase() != "usd"
              ) {
                lineObject.priceInTransactionCurrency = parseFloat(
                  this.netConvertToUsd ?? "0"
                );
              } else {
                lineObject.priceInTransactionCurrency =
                  lineObject.purchasePriceD365;
              }
            }
          }
        } else {
          lineObject.priceInTransactionCurrency = lineObject.purchasePriceD365;
        }
        this.patchValueInTotalNet(
          netRate,
          this.netReteAfterConversion.toString(),
          lineObject.orderedPurchaseQuantityD365?.toString() ?? "",
          lineObject
        );
      } else {
        lineObject.priceInTransactionCurrency = lineObject.purchasePriceD365;
      }
    } else {
      lineObject.purchasePriceD365 = 0;
      lineObject.lineAmountD365 = 0;
      lineObject.netConversion = 0;
      lineObject.netConversionTotal = 0;
      lineObject.priceInTransactionCurrency = 0;
      lineObject.amountInTransactionCurrency = 0;
    }
  }

  getCurrencyConvertionCode(currency: string) {
    if (currency) {
      return currency;
    } else {
      return "USD";
    }
  }

  onVendorSearchOpenToggle(
    mydrop: Popover,
    event: KeyboardEvent,
    target: HTMLElement
  ) {
    //if (event.target.value === "") {
    // this.parent.patchValue({
    //   vendor: {
    //     duplicateVendorDirectDelivery: '',
    //     vendorDirectDelivery: '',
    //     vendorPoAssignment: '',
    //     vendorName: '',
    //     vendorAddress: '',
    //     vendorPhone: '',
    //     vendorEmail: '',
    //     vendorFax: ''
    //   }
    // });
    //}

    const skipKeys = [
      "Tab",
      "Escape",
      "ArrowUp",
      "ArrowDown",
      "ArrowLeft",
      "ArrowRight",
    ];
    if (skipKeys.includes(event.key)) {
      return;
    }

    mydrop.show(event, target);
  }

  searchVendorName() {
    this.searchVendor$
      .pipe(
        debounceTime(1000),
        distinctUntilChanged(),
        switchMap((searchitem: string) => {
          const accountNumberOrName = searchitem;
          this.vendorList = [];
          if (accountNumberOrName != "" && accountNumberOrName != undefined) {
            return this._samplepoListingService.getVendorByNameAndAccountNumberFilter(
              accountNumberOrName
            );
          }
          return EMPTY;
        })
      )
      .subscribe({
        next: res => {
          if (res.data != null) {
            this.vendorList = res.data as unknown as GetAllVendorsDto[];
          }
        },
      });
  }

  onVendorRowSelection(
    vendorSelectedRow: GetAllVendorsDto,
    vendorToggle: Popover,
    serviceLine: PurchaseOrderLine
  ) {
    serviceLine.vendorNameD365 = vendorSelectedRow.vendorName ?? "";
    serviceLine.vendorIdD365 = vendorSelectedRow.vendorAccountNumber ?? "";
    this.vendorAccountNumber = vendorSelectedRow.vendorAccountNumber ?? "";
    serviceLine.currencyCodeD365 = vendorSelectedRow.currency || "USD";
    if (
      serviceLine.currencyCodeD365 &&
      serviceLine.currencyCodeD365?.toLowerCase() != "usd"
    ) {
      this.onCurrencyConversion(serviceLine);
    }

    this.clearNetInForeignCurrency(serviceLine);
    vendorToggle.hide();
  }

  onAddressAssignment(shipTo: {
    addressD365: string;
    addressLocationNumber: number;
    addressSelectionName: string;
    deliveryNameD365: string;
  }) {
    this.selectedServiceLines.forEach(x => {
      x.deliverySelectionD365 = shipTo.addressSelectionName;
      x.shipToTypeD365 = shipTo.addressLocationNumber;
      x.postalAddressD365 = shipTo.addressLocationNumber;
      x.shipToAddressD365 = shipTo.addressD365;
      x.deliveryNameD365 = shipTo.deliveryNameD365;
      x.addressDescriptionD365 = shipTo.deliveryNameD365;
    });
  }

  selectText(event: FocusEvent) {
    const input = event.target as HTMLInputElement;
    input.select();
  }

  onTextFieldKeyDown(
    event: KeyboardEvent,
    serviceLine: PurchaseOrderLine,
    field: string
  ) {
    const input = event.target as HTMLInputElement | HTMLTextAreaElement;
    const value = input.value;

    // If backspace or delete is pressed and field will become empty, set to empty string
    if (
      (event.key === "Backspace" || event.key === "Delete") &&
      (value.length === 1 ||
        (input.selectionStart === 0 && input.selectionEnd === value.length))
    ) {
      event.preventDefault();
      if (field === "externalItemNumberD365") {
        serviceLine.externalItemNumberD365 = "";
      } else if (field === "factoryNotesD365") {
        serviceLine.factoryNotesD365 = "";
      } else if (field === "lineDescriptionD365") {
        serviceLine.lineDescriptionD365 = "";
      }
      input.value = "";
    }
  }

  onQuantityKeyDown(event: KeyboardEvent, serviceLine: PurchaseOrderLine) {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    // If backspace or delete is pressed and field will become empty, set to 0
    if (
      (event.key === "Backspace" || event.key === "Delete") &&
      (value.length === 1 ||
        (input.selectionStart === 0 && input.selectionEnd === value.length))
    ) {
      event.preventDefault();
      serviceLine.orderedPurchaseQuantityD365 = 0;
      input.value = "0";
      input.select();

      this.onNetConversion(serviceLine.purchasePriceD365 ?? 0, serviceLine);
    }
  }

  onUnitPriceKeyDown(event: KeyboardEvent, serviceLine: PurchaseOrderLine) {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    // If backspace or delete is pressed and field will become empty, set to 0.0000
    if (
      (event.key === "Backspace" || event.key === "Delete") &&
      (value.length === 1 ||
        (input.selectionStart === 0 && input.selectionEnd === value.length))
    ) {
      event.preventDefault();
      serviceLine.purchasePriceD365 = 0;
      input.value = "0.0000";
      input.select();
      this.onNetConversion(serviceLine.purchasePriceD365, serviceLine);
    }
  }

  clearNetInForeignCurrency(serviceLine: PurchaseOrderLine) {
    serviceLine.purchasePriceD365 = 0;
    serviceLine.lineAmountD365 = 0;
    serviceLine.priceInTransactionCurrency = 0;
    serviceLine.amountInTransactionCurrency = 0;

    this.netConvertToUsd = "";
    this.netTotalPrice = "";
    this.netTotalPriceConverted = "";
  }

  onCurrencyConversion(serviceLine: PurchaseOrderLine) {
    this._loaderService.setLoaderState(true);
    this.clearNetInForeignCurrency(serviceLine);
    const currency = serviceLine.currencyCodeD365;
    if (currency) {
      const filter = {
        and: [
          {
            RateTypeName: "Closing",
          },
          {
            FromCurrency: currency,
          },
          {
            ToCurrency: "USD",
          },
        ],
      };
      const query = buildQuery({ filter });

      this._samplepoListingService
        .getCurrencyConversion(query)
        .pipe(untilDestroyed(this))
        .subscribe({
          next: res => {
            this._loaderService.setLoaderState(false);
            const sortedCurrency = res?.data?.sort((a, b) => {
              if (a.EndDate > b.EndDate) {
                return -1;
              }
              if (a.EndDate < b.EndDate) {
                return 1;
              }
              return 0;
            });
            if (sortedCurrency?.length > 0) {
              this.currencyConversion = sortedCurrency[0];
            }
          },
          error: () => {
            this._loaderService.setLoaderState(false);
          },
        });
    }
  }

  redirectToServicesForm(line: PurchaseOrderLine) {
    this.setServiceItemLine.emit(line);
  }

  formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = ("0" + (date.getMonth() + 1)).slice(-2);
    const day = ("0" + date.getDate()).slice(-2);
    return `${year}-${month}-${day}`;
  }
}
